import { Phone, MapPin, HeartHandshake, Wrench, Drill, Lamp } from 'lucide-react';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import PackageCard from '../components/PackageCard';
import FaqAccordion from '../components/FaqAccordion';
import { IMAGES, PACKAGES } from '../data/content';

const NEEDS = [
  {
    icon: HeartHandshake,
    title: 'Odd jobs run on local trust',
    text: 'Inviting someone into your home to fix things takes trust. A professional website with a friendly photo, real reviews and clear pricing signals turns a stranger with a drill into the neighbourhood go-to.',
  },
  {
    icon: MapPin,
    title: 'You show up for “handyman near me”',
    text: 'Handyman searches are among the most local of all — nobody wants a handyman from forty miles away. A complete Google Business Profile with your service areas set properly is what puts you on the map. We set it up and guide you through Google’s verification.',
  },
  {
    icon: Phone,
    title: 'Small jobs book fast when it’s easy',
    text: 'Someone with a wobbly door or a shelf to hang wants it sorted this week. Click-to-call, WhatsApp and a simple enquiry form on every page mean the quick jobs come to you.',
  },
];

const WORK_TYPES = [
  {
    icon: Wrench,
    title: 'Odd jobs and repairs',
    text: 'Doors, hinges, leaks, squeaks and everything in between — the no-job-too-small work that builds a loyal local customer base one fix at a time.',
  },
  {
    icon: Drill,
    title: 'Mounting and assembly',
    text: 'TVs, shelves, curtain rails, flat-pack furniture — high-frequency jobs people search for constantly. A clear services list means they know you do them before they call.',
  },
  {
    icon: Lamp,
    title: 'Landlord and rental maintenance',
    text: 'Steady repeat work from landlords and letting agents who need someone reliable on speed dial. A professional online presence gets you on their list.',
  },
];

const HANDYMAN_FAQS = [
  {
    q: 'I do dozens of different little jobs. How does a website cover all of that?',
    a: 'With a well-organised services list rather than dozens of pages. We group your work into clear categories — repairs, mounting and assembly, outdoor jobs, rental maintenance — each with a plain-English description, so every visitor quickly finds their odd job and sees that you do it.',
  },
  {
    q: 'My customers are mostly older or not very online. Is this still worth it?',
    a: 'Yes — because increasingly it’s their children and neighbours doing the searching on their behalf, and because “not very online” customers value a simple, trustworthy site more than anyone. Big text, obvious phone number, no nonsense: that’s exactly how we build it.',
  },
  {
    q: 'Can the site help me get regular landlord or letting agent work?',
    a: 'It gives you the credibility those customers need. Letting agents check a business properly before adding them to their contractor list — a professional website, Google profile with reviews and clear contact details is often the difference between being filed and being forgotten.',
  },
  {
    q: 'I’m in and out of customers’ houses all day. How much of my time does this take?',
    a: 'About an hour in total, spread across the build: one plain-English questionnaire, a few photos from your phone, and a look at the finished site before launch. Everything else is handled for you.',
  },
];

export default function HandymenPage() {
  return (
    <>
      <Seo
        title="Websites for Handymen — No Job Too Small, Found First | Build My Trade"
        description="Professional websites, Google Business Profile setup and social pages built specifically for UK handymen. Fixed packages from £249, plain English, everything handled."
        path="/websites-for-handymen"
        image={IMAGES.handymanHero.url}
      />

      {/* Hero */}
      <section className="bg-charcoal bg-blueprint" data-testid="handymen-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-32 sm:px-6 sm:pt-40 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">
              Websites for handymen
            </p>
            <h1 className="mt-5 text-balance font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-offwhite sm:text-5xl lg:text-6xl">
              No job too small. <span className="text-lime">No customer out of reach.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-offwhite/70 sm:text-lg">
              Build My Trade sets up everything a handyman needs to become the local go-to: a professional website
              with a clear list of everything you fix, a complete Google Business Profile and branded social pages —
              for one fixed, published price.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <ArrowLink to="/pricing" variant="lime" testid="handymen-cta-packages" className="px-7 py-3.5">
                See Fixed-Price Packages
              </ArrowLink>
              <ArrowLink to="/contact" variant="outline-light" testid="handymen-cta-quote" className="px-7 py-3.5">
                Get a Free Quote
              </ArrowLink>
            </div>
            <p className="mt-7 text-sm text-offwhite/50">
              Straightforward packages. Clear prices. No marketing jargon.
            </p>
          </div>
          <Reveal>
            <img
              src={IMAGES.handymanHero.url}
              alt={IMAGES.handymanHero.alt}
              loading="eager"
              className="aspect-[4/3] w-full rounded-2xl border border-charcoal-line object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* What a handyman's online presence needs */}
      <section className="bg-offwhite" data-testid="handymen-needs">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Built around how handyman work is won
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              What a handyman’s website has to do
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {NEEDS.map((n, i) => (
              <Reveal key={i} delay={0.08 * i} className="h-full">
                <div className="h-full rounded-2xl border border-hairline bg-white p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-charcoal">
                    <n.icon className="h-5 w-5 text-lime" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-charcoal">{n.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stonebrand">{n.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Work types */}
      <section className="border-y border-hairline bg-white" data-testid="handymen-work-types">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
                  Every odd job, clearly listed
                </p>
                <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
                  Set up so customers know exactly what you do
                </h2>
                <p className="mt-5 text-base leading-relaxed text-stonebrand">
                  The hardest part of marketing a handyman business is that you do everything. Your website organises
                  the lot into clear categories — so the customer with a sticking door finds their answer as fast as
                  the landlord with a long list.
                </p>
              </Reveal>
              <Reveal delay={0.15} className="mt-8">
                <img
                  src={IMAGES.handymanDetail.url}
                  alt={IMAGES.handymanDetail.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-2xl object-cover"
                />
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {WORK_TYPES.map((w, i) => (
                  <Reveal key={i} delay={0.08 * i}>
                    <li className="flex gap-5 rounded-2xl border border-hairline bg-offwhite-light p-7">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-lime">
                        <w.icon className="h-5 w-5 text-charcoal" aria-hidden="true" />
                      </span>
                      <div>
                        <h3 className="font-heading text-lg font-bold tracking-tight text-charcoal">{w.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-stonebrand">{w.text}</p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="bg-charcoal" data-testid="handymen-packages">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">Fixed, published prices</p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
              Packages that fit a handyman business
            </h2>
            <p className="mt-5 text-base leading-relaxed text-offwhite/65">
              The same straightforward packages we offer every trade — one-off payments, everything listed, nothing
              hidden. Get Found is a popular starting point for handymen: Google and social sorted properly, with a
              website ready to add when you want it.
            </p>
          </Reveal>
          <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3">
            {PACKAGES.map((pkg, i) => (
              <PackageCard key={pkg.slug} pkg={pkg} delay={0.08 * i} />
            ))}
          </div>
        </div>
      </section>

      {/* Handyman FAQs */}
      <section className="bg-offwhite" data-testid="handymen-faqs">
        <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Questions handymen ask us
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              Straight answers for busy handymen
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <FaqAccordion items={HANDYMAN_FAQS} testid="handymen-faq" />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal-deep bg-blueprint" data-testid="handymen-cta">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl">
                Ready to become the name <span className="text-lime">your neighbourhood trusts?</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-offwhite/65">
                Tell us about your handyman business and the areas you cover — we’ll recommend the right starting
                point, in plain English, with no obligation.
              </p>
              <div className="mt-9">
                <ArrowLink to="/contact" variant="lime" testid="handymen-cta-final" className="px-8 py-4">
                  Get Your Free Quote
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
